import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { useTranslation } from 'react-i18next'

interface BannerSlide {
  id: number
  heading: string
  gradient: string
  ctaText: string
  ctaTextLoggedIn: string
  textColor: 'light' | 'dark'
}

export function CompactHeroBanner() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentSlide, setCurrentSlide] = useState(0)

  const bannerSlides: BannerSlide[] = [
    {
      id: 1,
      heading: t('hero.slide1Heading'),
      gradient: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
      ctaText: t('hero.slide1Cta'),
      ctaTextLoggedIn: t('hero.slide1CtaLoggedIn'),
      textColor: 'light',
    },
    {
      id: 2,
      heading: t('hero.slide2Heading'),
      gradient: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
      ctaText: t('hero.slide2Cta'),
      ctaTextLoggedIn: t('hero.slide2CtaLoggedIn'),
      textColor: 'light',
    },
    {
      id: 3,
      heading: t('hero.slide3Heading'),
      gradient: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
      ctaText: t('hero.slide3Cta'),
      ctaTextLoggedIn: t('hero.slide3CtaLoggedIn'),
      textColor: 'light',
    }
  ]

  const handleCTA = () => {
    if (!user) {
      sessionStorage.setItem('post_login_redirect', '/my-books')
      navigate('/login')
    } else {
      navigate('/my-books')
    }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length)
    }, 5000)

    return () => clearInterval(timer)
  }, [])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  return (
    <div className="compact-hero-banner relative w-full h-[80px] overflow-hidden rounded-xl bg-gradient-to-br from-primary/20 via-primary/10 to-background">
      {/* Banner Slides */}
      {bannerSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`banner-slide absolute inset-0 transition-opacity duration-500 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background - Gradient */}
          <div className="banner-background absolute inset-0">
            <div
              className="w-full h-full"
              style={{ background: slide.gradient }}
            />
          </div>

          {/* Content Container - Single Row */}
          <div className="banner-content relative h-full flex items-center">
            <div className="container mx-auto px-6 md:px-12 lg:px-16">
              <div className="flex items-center justify-between gap-4">
                {/* Heading */}
                <h1 className={`banner-heading text-lg sm:text-xl md:text-2xl font-bold leading-tight flex-1 ${
                  slide.textColor === 'dark' ? 'text-gray-900' : 'text-white'
                }`}>
                  {slide.heading}
                </h1>

                {/* CTA Button */}
                <Button 
                  size="default" 
                  className="cta-button bg-primary hover:bg-primary/90 text-primary-foreground text-sm md:text-base px-4 md:px-6 py-2 rounded-lg font-semibold transition-all hover:scale-105 shadow-xl whitespace-nowrap"
                  onClick={handleCTA}
                >
                  {user ? slide.ctaTextLoggedIn : slide.ctaText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Dots */}
      <div className="nav-dots absolute bottom-2 right-3 flex gap-2 z-10">
        {bannerSlides.map((_, index) => (
          <button
            key={index}
            className={`nav-dot w-2 h-2 rounded-full transition-all ${
              index === currentSlide
                ? 'bg-white scale-125'
                : 'bg-white/50 hover:bg-white/75'
            }`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  )
}

